import React, { useState, useEffect } from "react";
import { PartyPopper, ExternalLink, Check, Trash2, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { colors } from "@/lib/theme";
import { Spinner } from "@/components/peeps/ui";

export function PartifulConnect() {
  const [state, setState] = useState({ connected: false, url: null, count: 0 });
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.getPartiful().then(setState).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const connect = async () => {
    if (!url.trim()) { toast("Paste your Partiful calendar link first"); return; }
    setBusy(true);
    try {
      const r = await api.connectPartiful(url.trim());
      setState(r); setUrl("");
      toast(`Partiful connected — ${r.count} events imported`);
    } catch (e) { toast(e.message || "Couldn't connect that link"); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect your Partiful calendar? Your events will stop showing to your groups.")) return;
    setBusy(true);
    try { await api.disconnectPartiful(); setState({ connected: false, url: null, count: 0 }); toast("Partiful disconnected"); }
    catch (e) { toast(e.message || "Failed"); }
    finally { setBusy(false); }
  };

  const card = { background: colors.surfaceSecondary, border: `1px solid ${colors.border}`, borderRadius: 12 };

  return (
    <div data-testid="partiful-connect" style={card} className="p-4">
      <div className="flex items-center gap-2 mb-1">
        <PartyPopper size={17} color={colors.brandSecondary} />
        <span className="font-display font-bold text-[15px]" style={{ color: colors.onSurface }}>Partiful</span>
        {state.connected && (
          <span className="ml-auto font-display text-[11px] px-2 py-0.5 rounded-full flex items-center gap-1" style={{ background: colors.success + "22", color: colors.success }}>
            <Check size={11} /> {state.count} events
          </span>
        )}
      </div>
      <p className="font-display text-[12.5px] leading-relaxed mb-3" style={{ color: colors.onSurfaceTertiary }}>
        Bring your Partiful plans into peeps. Open Partiful, copy your <b>calendar link</b>, and paste it below — your events show on your personal calendar, and when friends import the same event peeps shows how many are going.
      </p>

      <a href="https://partiful.com/events?profile-settings=calendar-sync" target="_blank" rel="noreferrer" data-testid="partiful-open"
        className="flex items-center justify-center gap-2 w-full rounded-xl py-2.5 mb-2" style={{ background: colors.surfaceTertiary, border: `1px solid ${colors.border}` }}>
        <ExternalLink size={14} color={colors.brandSecondary} />
        <span className="font-display font-medium text-[13px]" style={{ color: colors.onSurface }}>Open Partiful to copy your link</span>
      </a>

      {loading ? (
        <div className="flex justify-center py-3"><Spinner /></div>
      ) : (
        <>
          <div className="flex items-center gap-2 rounded-xl px-3 py-2 mb-2" style={{ background: colors.surfaceTertiary }}>
            <Link2 size={14} color={colors.muted} />
            <input
              data-testid="partiful-url-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={state.connected ? "Paste a new link to refresh" : "webcal://calendars.partiful.com/..."}
              className="flex-1 bg-transparent outline-none font-mono2 text-[11px]"
              style={{ color: colors.onSurface }}
            />
          </div>
          <div className="flex gap-2">
            <button data-testid="partiful-connect-btn" onClick={connect} disabled={busy}
              className="flex items-center justify-center gap-2 flex-1 rounded-xl py-2.5" style={{ background: colors.brand }}>
              {busy ? <Spinner style={{ borderTopColor: colors.onBrand }} /> : <Check size={14} color={colors.onBrand} />}
              <span className="font-display font-bold text-[13px]" style={{ color: colors.onBrand }}>{state.connected ? "Refresh" : "Connect"}</span>
            </button>
            {state.connected && (
              <button data-testid="partiful-disconnect-btn" onClick={disconnect} disabled={busy}
                className="flex items-center justify-center rounded-xl py-2.5 px-4" style={{ background: colors.surfaceTertiary, border: `1px solid ${colors.border}` }}>
                <Trash2 size={14} color={colors.danger || "#e5484d"} />
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
